import { Chess } from "chess.js";
import { PgnMove } from "./PgnMove";
import { PgnParser } from "./PgnParser";
import { CHESS_START_POSITION } from "../constants";
import { chessgroundToChessBase, mcqToChessBase, orientToChessBase, } from "./Annotation";
export class PgnData {
    _fen;
    _pgn;
    _rootMove;
    _chess;
    _preMove;
    _validationErrors;
    constructor(fen, pgn, rootMove, chess, preMove, validationErrors = []) {
        this._fen = fen;
        this._pgn = pgn;
        this._rootMove = rootMove;
        this._chess = chess;
        this._preMove = preMove;
        this._validationErrors = validationErrors;
    }
    static createEmpty(chess) {
        const chessInstance = chess || new Chess();
        chessInstance.reset(); // Ensure we start from initial position
        const preMove = PgnMove.createPreMove(CHESS_START_POSITION);
        return new PgnData(CHESS_START_POSITION, "", null, chessInstance, preMove);
    }
    static createFromChessInstance(chess) {
        if (!chess) {
            throw new Error("Chess instance is required");
        }
        const preMove = PgnMove.createPreMove(chess.fen());
        return new PgnData(chess.fen(), chess.pgn(), null, chess, preMove);
    }
    static create(pgn, chess, builder) {
        const fen = PgnParser.extractInitialFen(pgn);
        const { rootMove, preMove } = builder.parseMoves();
        return new PgnData(fen, pgn, rootMove, chess, preMove, builder.validationErrors);
    }
    get fen() {
        return this._fen;
    }
    get pgn() {
        return this._pgn;
    }
    get preMove() {
        return this._preMove;
    }
    /**
     * Find the anchor move that has a puzzleAnnotation attached.
     * This is the move immediately before the {[#]} marker (or preMove if the
     * marker appears before any moves). Returns null for non-puzzle PGNs.
     */
    findPuzzleStartMove() {
        if (this._preMove.puzzleAnnotation)
            return this._preMove;
        return this.moves.find((m) => m.puzzleAnnotation) ?? null;
    }
    /**
     * @deprecated Use `preMove.comment` instead
     */
    get preGameComment() {
        return this._preMove?.comment;
    }
    /**
     * @deprecated Use `preMove.shapes` instead
     */
    get preGameShapes() {
        return this._preMove?.shapes;
    }
    /**
     * @deprecated Use `preMove.mcq` instead
     */
    get preGameMcq() {
        return this._preMove?.mcq;
    }
    /**
     * Gets a move at the specified index in the main line
     * @param index The zero-based index of the move to get
     * @returns The move at the specified index, or null if not found
     */
    getMoveByIndex(index) {
        if (index < 0 || !this._rootMove) {
            return null;
        }
        let currentMove = this._rootMove;
        let currentIndex = 0;
        while (currentMove) {
            if (currentIndex === index) {
                return currentMove;
            }
            currentMove = currentMove.nextMove;
            currentIndex++;
        }
        return null;
    }
    /**
     * Returns an array of moves in the main line (excluding variations)
     */
    get moves() {
        if (!this._rootMove) {
            return [];
        }
        const moves = [];
        let currentMove = this._rootMove;
        while (currentMove) {
            moves.push(currentMove);
            currentMove = currentMove.nextMove;
        }
        return moves;
    }
    get rootMove() {
        return this._rootMove;
    }
    get rows() {
        return this.getRows();
    }
    getRows(options = {}) {
        return this._rootMove?.rows(options) || [];
    }
    get firstTurn() {
        return this._fen.split(" ")[1];
    }
    get isPuzzle() {
        return PgnParser.isPuzzle(this.pgn);
    }
    get validationErrors() {
        return this._validationErrors;
    }
    get hasInvalidMoves() {
        return this._validationErrors.length > 0;
    }
    /**
     * Builds the puzzle marker string from a PuzzleAnnotation.
     * Outputs "{[#]}" for default settings, or "{[# key="val" ...]}" with non-default values.
     */
    buildPuzzleMarker(annotation) {
        const parts = [];
        if (annotation.mateIn !== 1)
            parts.push(`mate_in="${annotation.mateIn}"`);
        if (annotation.aiLevel !== 0)
            parts.push(`ai_level="${annotation.aiLevel}"`);
        if (annotation.attempts !== null)
            parts.push(`attempts="${annotation.attempts}"`);
        return parts.length > 0 ? `{[# ${parts.join(" ")}]}` : "{[#]}";
    }
    /**
     * Builds a string representation of the moves in the PGN.
     * The anchor move (with puzzleAnnotation) emits the {[#]} marker as a suffix,
     * so it appears between the anchor and the first puzzle move in the output.
     * preMove anchors are handled separately in toString().
     */
    buildMovesString(move, options = {}) {
        const { includeUserScore = true, stopAtMoveId } = options;
        if (!move)
            return "";
        // Stop if we've reached the specified move
        if (stopAtMoveId && move.id === stopAtMoveId) {
            return move.toString({ includeUserScore }) + " ";
        }
        let result = "";
        // Add move number if it's white's move or first move in a variation
        if (move.color === "w" || !move.previousMoveInline) {
            result += `${move.number}${move.color === "w" ? "." : "..."} `;
        }
        // Add the move itself
        result += move.toString({ includeUserScore }) + " ";
        // If this move is the puzzle anchor, emit the {[#]} marker immediately after it.
        // This places the marker between the anchor and the first puzzle move.
        if (move.puzzleAnnotation) {
            result += this.buildPuzzleMarker(move.puzzleAnnotation) + " ";
        }
        // Don't process further moves if we've reached the stop move
        if (stopAtMoveId && move.id === stopAtMoveId) {
            return result;
        }
        if (move.childMoves && move.childMoves.length > 0) {
            for (const childMove of move.childMoves) {
                result +=
                    "(" + this.buildMovesString(childMove, options).trim() + ") ";
            }
        }
        if (move.nextMove) {
            result += this.buildMovesString(move.nextMove, options);
        }
        return result;
    }
    /**
     * Gets just the moves text without headers or pre-game comments
     */
    get movesString() {
        if (!this.rootMove)
            return "";
        return this.buildMovesString(this.rootMove).trim();
    }
    /**
     * Gets a simplified header string without GameId, Setup, Homework, and empty values
     */
    get simplifiedHeader() {
        return PgnParser.getSimplifiedHeaders(this._pgn);
    }
    get userMoves() {
        // traverse through moves and childMoves
        const userMoves = [];
        const traverse = (move) => {
            if (!move)
                return;
            if (move.childMoves) {
                for (const childMove of move.childMoves) {
                    traverse(childMove);
                }
            }
            if (move.userScore != null) {
                userMoves.push(move);
            }
            traverse(move.nextMove);
        };
        traverse(this.rootMove);
        return userMoves;
    }
    /**
     * Gets the move for preview - returns the last user move if available,
     * for puzzles returns the move at the [#] marker (puzzle starting position),
     * otherwise returns the last move for non-puzzles
     */
    get previewMove() {
        // If there are user moves, return the last user move
        if (this.userMoves.length > 0) {
            return this.userMoves[this.userMoves.length - 1];
        }
        if (this.isPuzzle) {
            // For puzzles, the puzzle starts at the anchor move (the move with puzzleAnnotation).
            // If the anchor is preMove (id=""), return null so previewFen falls back to this.fen.
            const anchor = this.findPuzzleStartMove();
            if (anchor && anchor.id !== "") {
                return anchor;
            }
            return null;
        }
        // If the initial FEN is not the standard starting position, return the first move
        const headerFen = this.fen;
        if (headerFen !== CHESS_START_POSITION) {
            return this.rootMove;
        }
        // Otherwise, return the last move
        if (this.moves.length > 0) {
            return this.moves[this.moves.length - 1];
        }
        return null;
    }
    /**
     * Gets the FEN for preview - returns the FEN of the preview move if available, otherwise returns the initial FEN
     */
    get previewFen() {
        // If there are preview move, return the FEN of the last preview move
        if (this.previewMove) {
            return this.previewMove.fen;
        }
        // Otherwise, return the initial FEN
        return this.fen;
    }
    /**
     * Generates a PGN string representation of the chess game
     * @param {Object} options - Configuration options
     * @param {boolean} [options.includeUserScore=true] - Whether to include user scores in the output
     * @param {string} [options.stopAtMoveId] - If provided, stops generating moves after this move ID
     * @returns {string} The PGN string
     */
    toString(options = {}) {
        const normalizedHeader = PgnParser.normalizeHeaders(this._pgn) || "";
        // Generate moves section using the extracted method
        let movesText = "";
        if (this.rootMove) {
            const { includeUserScore, stopAtMoveId } = options;
            movesText = this.buildMovesString(this.rootMove, {
                includeUserScore,
                stopAtMoveId,
            });
        }
        // Build pre-game annotations from preMove
        let preGameAnnotations = "";
        if (this._preMove) {
            // Add orient annotation if present on preMove
            if (this._preMove.orient) {
                preGameAnnotations += `{${orientToChessBase(this._preMove.orient)}} `;
            }
            // Add shapes annotation if present
            if (this._preMove.shapes && this._preMove.shapes.length > 0) {
                const annotationString = chessgroundToChessBase(this._preMove.shapes);
                if (annotationString) {
                    preGameAnnotations += `{${annotationString}} `;
                }
            }
            // Add MCQ annotation if present
            if (this._preMove.mcq) {
                const mcqString = mcqToChessBase(this._preMove.mcq);
                if (mcqString) {
                    preGameAnnotations += `{${mcqString}} `;
                }
            }
            // Add puzzle marker if preMove is the anchor (puzzle starts from initial position)
            if (this._preMove.puzzleAnnotation) {
                preGameAnnotations +=
                    this.buildPuzzleMarker(this._preMove.puzzleAnnotation) + " ";
            }
            // Add comment if present
            if (this._preMove.comment) {
                preGameAnnotations += `{${this._preMove.comment}} `;
            }
        }
        // Combine headers and moves
        return normalizedHeader + "\n\n" + preGameAnnotations + movesText.trim();
    }
    revealMove(moveId) {
        const move = this.rootMove?.findMoveById(moveId);
        if (move) {
            move.recordRevealed();
        }
    }
    /**
     * @deprecated Use `preMove.updateShapes(shapes)` instead
     */
    updatePreGameShapes(shapes) {
        this._preMove.shapes = shapes.length > 0 ? shapes : undefined;
    }
    addMove(san, previousMoveId) {
        const previousMove = previousMoveId
            ? this._rootMove?.findMoveById(previousMoveId)
            : null;
        // throw error if previousMoveId is not null and not found
        if (previousMoveId && !previousMove) {
            throw new Error(`Invalid previous move ID: ${previousMoveId}`);
        }
        const parentMove = previousMove ? previousMove.nextMove : this._rootMove;
        try {
            const newMove = new PgnMove(san, this._chess, previousMove, parentMove, this.fen);
            if (!newMove?.id) {
                throw new Error(`Invalid move: ${san} from ${previousMoveId}`);
            }
            if (!this._rootMove) {
                this._rootMove = newMove;
                // Back-link root move to preMove so puzzleRole chain can reach the anchor
                newMove.setPreMove(this._preMove);
            }
            return newMove;
        }
        catch (error) {
            if (error.message.includes("Duplicate move:")) {
                // If it's a duplicate of parent move
                if (parentMove?.san === san) {
                    return parentMove;
                }
                // If it's a duplicate of a variation
                const existingMove = parentMove?.childMoves?.find((m) => m.san === san);
                if (existingMove) {
                    return existingMove;
                }
            }
            throw error;
        }
    }
}
